import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Project image carousel with subtle prev/next arrows and step dots.
export const ProjectSequence = ({ project }) => {
  const [active, setActive] = useState(0);
  const count = project.steps.length;
  const step = project.steps[active];
  const go = (dir) => setActive((a) => (a + dir + count) % count);

  return (
    <div data-testid={`project-${project.id}`} className="w-full">
      <div className="group relative aspect-[4/3] w-full overflow-hidden bg-secondary">
        <AnimatePresence mode="wait">
          <motion.img
            key={step.url}
            src={step.url}
            alt={`${project.name} — ${step.label}`}
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            loading="lazy"
            className={`absolute inset-0 h-full w-full ${
              step.contain ? "object-contain bg-white" : "object-cover"
            }`}
          />
        </AnimatePresence>

        <div className="absolute left-0 top-0 m-3 bg-white/70 px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] text-charcoal/70 backdrop-blur-sm">
          {step.label}
        </div>

        {count > 1 && (
          <>
            <button
              data-testid={`project-${project.id}-prev`}
              onClick={() => go(-1)}
              aria-label="Previous image"
              className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/70 text-charcoal opacity-60 backdrop-blur-sm transition-all duration-200 hover:bg-white hover:opacity-100"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              data-testid={`project-${project.id}-next`}
              onClick={() => go(1)}
              aria-label="Next image"
              className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/70 text-charcoal opacity-60 backdrop-blur-sm transition-all duration-200 hover:bg-white hover:opacity-100"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}
      </div>

      <div className="mt-4 flex items-end justify-between gap-4">
        <div>
          <h3 className="text-lg font-medium tracking-tight text-charcoal sm:text-xl">
            {project.name}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">{project.meta}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5 pb-1">
          {project.steps.map((s, i) => (
            <button
              key={s.label}
              data-testid={`project-${project.id}-step-${i}`}
              onClick={() => setActive(i)}
              aria-label={s.label}
              className={`h-1.5 w-1.5 rounded-full transition-colors duration-200 ${
                i === active ? "bg-charcoal" : "bg-border hover:bg-muted-foreground"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
